import { useEffect, useRef, useState } from 'react';

interface Position {
  x: number;
  y: number;
}

const DRAG_THRESHOLD = 3;
// Keep at least this much of the box on-screen vertically so the draggable header is reachable.
const MIN_VISIBLE_EDGE = 40;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function loadStoredPos(storageKey: string): Position | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Position>;
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    // Ignore malformed/unavailable storage.
  }
  return null;
}

/**
 * Makes a fixed-position overlay box draggable by its header and persists the
 * dragged position to localStorage. Until the user drags (or if nothing is
 * stored), `style` is undefined so the box keeps whatever default corner its CSS
 * defines.
 *
 * Usage:
 *   const { containerRef, style, dragHandlers, wasDragged } = useDraggablePosition('myBox:pos');
 *   <div ref={containerRef} style={style}>
 *     <button onClick={() => { if (wasDragged()) return; toggle(); }} {...dragHandlers}>…</button>
 */
export function useDraggablePosition(storageKey: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ originX: number; originY: number; startX: number; startY: number; width: number } | null>(
    null,
  );
  const movedRef = useRef(false);
  const [pos, setPos] = useState<Position | null>(null);
  // Mirrors `pos` so onPointerUp can read the latest value synchronously, without
  // depending on the (possibly batched) state update from the last onPointerMove.
  const posRef = useRef<Position | null>(null);

  const updatePos = (next: Position) => {
    posRef.current = next;
    setPos(next);
  };

  useEffect(() => {
    const stored = loadStoredPos(storageKey);
    if (!stored) return;
    const width = containerRef.current?.offsetWidth ?? 0;
    updatePos({
      x: clamp(stored.x, 0, window.innerWidth - width),
      y: clamp(stored.y, 0, window.innerHeight - MIN_VISIBLE_EDGE),
    });
  }, [storageKey]);

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    movedRef.current = false;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) movedRef.current = true;
    updatePos({
      x: clamp(drag.originX + dx, 0, window.innerWidth - drag.width),
      y: clamp(drag.originY + dy, 0, window.innerHeight - MIN_VISIBLE_EDGE),
    });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer may already be released.
    }
    if (movedRef.current && posRef.current) {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(posRef.current));
      } catch {
        // Ignore unavailable storage.
      }
    }
  };

  // Returns true (once) if the just-finished gesture was a drag, so callers can
  // suppress the click action that would otherwise fire on pointer-up.
  const wasDragged = () => {
    if (movedRef.current) {
      movedRef.current = false;
      return true;
    }
    return false;
  };

  // Inline left/top must override any CSS default corner (e.g. bottom/right).
  const style: React.CSSProperties | undefined = pos
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : undefined;

  return { containerRef, style, dragHandlers: { onPointerDown, onPointerMove, onPointerUp }, wasDragged };
}
